import type { Translation } from '../../types';
import type { ux as en } from '../en/ux';

export const ux: Translation<typeof en> = {
  today: {
    title: 'Cần xử lý hôm nay',
    open: 'Mở',
    none: 'Không có gì đang chờ',
    dossiers: { label: 'Hồ sơ chờ duyệt', hint: 'Tên miền chờ duyệt hồ sơ để kích hoạt' },
    work: { label: 'Việc đang mở', hint: 'Xác minh danh tính, hồ sơ và ngoại lệ' },
    expiring: { label: 'Hết hạn trong 30 ngày', hint: 'Tên miền đang hoạt động cần gia hạn hoặc nhắc khách' },
    unresolved: { label: 'Lệnh registry chưa rõ kết quả', hint: 'Đang đối soát; không gửi lại thủ công' },
  },
  sidebar: {
    groups: {
      work: 'Làm việc',
      commerce: 'Thương mại',
      infra: 'Vận hành & quản trị',
      domains: 'Tên miền',
      catalog: 'Bán tên miền',
      registry: 'Registry & tuân thủ',
      backorder: 'Backorder',
      auctions: 'Đấu giá tên miền',
      shared: 'Dùng chung',
      /** Các mục do tiện ích đang chạy thêm vào (`nav/addonMenu.ts`). */
      addons: 'Tiện ích',
    },
    entries: {
      home: 'Việc cần xử lý',
      domains: 'Tên miền',
      dossiers: 'Hồ sơ',
      subjects: 'Chủ thể',
      customers: 'Khách hàng',
      orders: 'Đơn hàng',
      invoices: 'Hoá đơn & thanh toán',
      ledger: 'Ví & sổ cái',
      products: 'Sản phẩm & giá',
      addons: 'Add-on & phí',
      quote: 'Tính thử báo giá',
      registry: 'Registry',
      compliance: 'Tuân thủ & VNNIC',
      ops: 'Vận hành & audit',
      settings: 'Cài đặt',
      backorder: 'Trung tâm backorder',
      drops: 'Tên miền sắp giải phóng',
      backorders: 'Yêu cầu backorder',
      catching: 'Giành đăng ký',
      holds: 'Giữ chỗ & hoàn trả',
      auctions: 'Phiên đấu giá',
      auctionSessions: 'Tạo & quản lý phiên',
      bids: 'Lượt đặt giá & audit',
      settlement: 'Quyết toán',
      handover: 'Bàn giao tên miền',
    },
  },
  addons: {
    lifecycle: 'Vòng đời add-on',
    lifecycleHint: 'Mỗi add-on có trạng thái riêng, tách khỏi tên miền và khỏi thanh toán.',
    step: {
      order: 'Đã đặt',
      orderHint: 'Mọi add-on của tên miền',
      wait: 'Chờ áp dụng',
      waitHint: 'Đã đặt, chưa có hiệu lực',
      active: 'Đang dùng',
      activeHint: 'Đã áp dụng, gia hạn cùng tên miền',
      end: 'Đã kết thúc',
      endHint: 'Đã huỷ hoặc hết hạn',
    },
    cost: 'Chi phí gia hạn 1 năm',
    costHint: 'Bảng giá mặc định của TLD này, gồm phí và add-on thu khi gia hạn.',
    costError: 'Không tải được chi phí gia hạn',
    noCost: 'TLD này chưa có giá gia hạn.',
    total: 'Tổng',
    history: 'Lịch sử dịch vụ',
    noHistory: 'Chưa có add-on nào.',
    event: { ordered: 'Đã đặt {{name}}', applied: 'Đã áp dụng {{name}}', ended: 'Đã kết thúc {{name}}' },
    onNotDoneTitle: 'Đã đặt chưa có nghĩa là đã chạy',
    onNotDone: 'Add-on chỉ tính là đang dùng khi đã được áp dụng. DNSSEC chỉ hiện "đã ký" khi bản ghi DS đã có tại registry.',
  },
  dnssec: {
    loadError: 'Không tải được DNSSEC',
    state: 'DNSSEC',
    signed: 'Đã ký',
    unsigned: 'Chưa ký',
    stateHint: 'Theo bản ghi DS tại registry',
    ds: 'Bản ghi DS tại registry',
    keyTags: 'Key tag',
    digest: 'Kiểu digest',
    noKeys: 'Chưa có khoá',
    chain: 'Chuỗi xác thực',
    chainHint: 'Những gì console biết về việc ký của tên miền này.',
    gate: {
      tld: 'TLD hỗ trợ DNSSEC',
      tldNo: 'Registry của TLD này không nhận bản ghi DS.',
      ds: 'Bản ghi DS tại registry',
      dsHint: '{{n}} bản ghi đã công bố ở vùng cha.',
      dsNone: 'Chưa có: tên miền chưa được ký ở vùng cha.',
      keys: 'DNSKEY và chữ ký trong zone',
      keysHint: 'Do bên vận hành DNS kiểm tra; console không truy vấn resolver.',
    },
  },
  privacy: {
    loadError: 'Không tải được',
    serviceTitle: 'Ẩn thông tin WHOIS',
    serviceHint: 'Là add-on có kỳ hạn riêng. Việc che dữ liệu theo quy định vẫn áp dụng dù có hay không.',
    state: 'Trạng thái',
    addon: { ORDERED: 'Đã đặt, chưa áp dụng', ACTIVE: 'Đang dùng', CANCELLED: 'Đã huỷ', ENDED: 'Đã kết thúc' },
    notUsed: 'Tên miền này chưa dùng',
    notOffered: 'TLD này không cung cấp',
    period: 'Kỳ hạn',
    orderable: 'Có thể đặt',
    yes: 'Có',
    noPrice: 'Chưa có giá cho khách này',
    manage: 'Đặt hoặc huỷ ở Add-on & dịch vụ',
    previewTitle: 'RDAP / WHOIS công bố gì',
    previewHint: 'Bản ghi công khai của tên miền theo chính sách công bố đang áp dụng. Dữ liệu cá nhân không bao giờ hiện ở đây.',
    policy: 'Chính sách {{key}} · phiên bản {{version}}',
    noPolicy: 'Chưa ghi nhận chính sách công bố',
    notPublished: 'Tên miền này không có trong bản ghi công khai.',
    col: { field: 'Trường', who: 'Thuộc', shown: 'Công bố dạng', value: 'Giá trị công khai' },
    registrant: 'Chủ thể',
    other: 'Tên miền',
    shown: { PUBLIC: 'Công khai', REDACTED: 'Che', CONTACT_FORM: 'Qua biểu mẫu liên hệ', OMITTED: 'Bỏ qua' },
    twoTitle: 'Hai bản ghi khác nhau',
    internal: 'Hồ sơ nội bộ',
    internalHint: 'Chủ thể thật, liên hệ và giấy tờ; nhân viên xem theo vai trò.',
    public: 'RDAP / WHOIS công khai',
    publicHint: 'Các trường chính sách cho phép; còn lại bị che hoặc qua biểu mẫu liên hệ.',
    notSameTitle: 'Ẩn thông tin khác với che dữ liệu',
    notSame: 'Việc che dữ liệu mà chính sách bắt buộc không bao giờ bán như dịch vụ ẩn thông tin. Yêu cầu cung cấp dữ liệu được xử lý như vụ việc, có căn cứ pháp lý và nhật ký.',
    moreTitle: 'Liên quan',
    linkPolicy: 'Chính sách công bố (RDAP)',
    linkDisclosure: 'Yêu cầu cung cấp dữ liệu',
  },
  quote: {
    title: 'Tính thử báo giá',
    subtitle: 'Một nghiệp vụ của TLD tốn của khách bao nhiêu theo bảng giá, từng dòng: dịch vụ, phí thu hộ và add-on. Không lưu gì.',
    tld: 'TLD',
    book: 'Bảng giá',
    defaultBook: 'Mặc định (bán lẻ)',
    years: 'Kỳ hạn',
    yearsN_one: '{{count}} năm',
    yearsN_other: '{{count}} năm',
    operation: 'Nghiệp vụ',
    usedBook: 'Tính theo bảng giá {{book}}.',
    loadError: 'Không tải được giá',
    none: 'TLD này chưa có nghiệp vụ nào có giá.',
    cannot: 'Chưa tính được giá {{op}}',
    lines: '{{op}} · {{years}} năm',
    linesHint: 'Đã gồm phí bắt buộc và add-on mặc định của TLD; add-on tuỳ chọn không tính.',
    noLines: 'Không có dòng nào.',
    col: { item: 'Khoản mục', kind: 'Loại', amount: 'Đơn giá', discount: 'Giảm giá', tax: 'Thuế', total: 'Thành tiền' },
    totals: 'Tổng',
    registryCost: 'Giá vốn registry',
    subtotal: 'Trước thuế',
    tax: 'Thuế',
    total: 'Khách trả',
    costNote: 'Giá vốn registry là số ta trả; không phải một dòng trên hoá đơn của khách.',
    feesTitle: 'Phí là dòng riêng',
    fees: 'Lệ phí nhà nước và phí ICANN là dòng riêng và không bao giờ được giảm. Đăng ký .vn có lệ phí đăng ký một lần và phí duy trì theo năm.',
    whereTitle: 'Muốn đổi giá',
    where: 'Giá, phí và add-on mà TLD cung cấp được đặt ở trang TLD.',
    whereLink: 'Mở giá của TLD',
  },
  catalogue: {
    related: 'Trang liên quan',
    linkQuote: 'Tính thử báo giá',
    linkPrices: 'Bảng giá',
    linkTlds: 'TLD',
    addons: 'Dịch vụ bán kèm',
    addonsHint: 'Bán cùng tên miền, theo từng TLD',
    fees: 'Loại phí',
    feesHint: 'Lệ phí nhà nước, phí ICANN và phí nghiệp vụ',
    tlds: 'TLD có add-on',
    tldsHint: 'Bắt buộc, mặc định hoặc tuỳ chọn',
    inUse: 'Add-on đang dùng',
    inUseHint: 'Trên các tên miền · danh mục {{version}}',
  },
  modules: {
    label: 'Không gian quản lý',
    pick: 'Chọn phân hệ',
    platform: 'Nền tảng & thương mại',
    domains: 'Domain Registrar',
    hosting: 'Hosting cPanel',
    email: 'TinoMail Business',
    cloud: 'Cloud Instant',
    backorder: 'Backorder',
    auctions: 'Đấu giá tên miền',
  },
  settings: {
    searchLabel: 'Tìm cài đặt',
    searchPlaceholder: 'Gõ thứ cần đổi: giá, eKYC, e-mail, nhân viên…',
    none: 'Không có cài đặt nào khớp “{{q}}”.',
    open: 'Mở',
    groups: {
      sell: { label: 'Bán tên miền', description: 'TLD, giá, khuyến mãi và dịch vụ: khách mua được gì và giá bao nhiêu.' },
      connect: { label: 'Kết nối', description: 'Registry, VNNIC, xác minh danh tính, e-mail và gửi sự kiện.' },
      org: { label: 'Tổ chức & nhân viên', description: 'Ai được đăng nhập, được làm gì và đã làm gì.' },
      compliance: { label: 'Tuân thủ', description: 'Giấy tờ, mẫu bản khai, quy định đuôi .vn, chính sách và hạn báo cáo.' },
    },
    items: {
      tlds: { label: 'TLD đang bán', description: 'Từng TLD cùng mức sẵn sàng; thêm và thiết lập TLD mới.' },
      channels: { label: 'Kênh đăng ký TLD', description: 'Mỗi TLD được đăng ký qua kết nối nào.' },
      costs: { label: 'Chi phí registry & tỷ giá', description: 'Giá vốn mỗi lần đăng ký, gia hạn, chuyển về.' },
      prices: { label: 'Bảng giá bán', description: 'Bảng giá theo hạng và giá riêng của khách hàng theo từng TLD.' },
      promotions: { label: 'Khuyến mãi', description: 'Chương trình, mã giảm giá và lượt dùng.' },
      services: { label: 'Dịch vụ tên miền', description: 'Dịch vụ bán kèm tên miền và giá.' },
      tax: { label: 'Thuế suất', description: 'Thuế GTGT theo sản phẩm và thời kỳ.' },
      lifecycle: { label: 'Vòng đời, chuyển về & khôi phục', description: 'Thời gian ân hạn, phí khôi phục và DNS khi hết hạn theo TLD.' },
      parking: { label: 'DNS đỗ', description: 'Máy chủ tên dùng khi tên miền chưa có máy chủ tên riêng.' },
      catalog: { label: 'Danh mục TLD', description: 'Mọi TLD trên thế giới, để chọn TLD mới để bán.' },
      registries: { label: 'Registry & nhà đăng ký gốc', description: 'Thông tin đăng nhập, chứng chỉ và địa chỉ của từng kết nối registry.' },
      vnnic: { label: 'VNNIC', description: 'Kết nối nhà đăng ký với VNNIC.' },
      ekyc: { label: 'Xác minh danh tính (eKYC)', description: 'Xác minh danh tính chủ thể qua VNNIC.' },
      hstm: { label: 'Hồ sơ tên miền tại VNNIC', description: 'Gửi hồ sơ tên miền .vn sang VNNIC.' },
      whois: { label: 'WHOIS', description: 'Dịch vụ WHOIS của VNNIC và chứng chỉ.' },
      ote: { label: 'Bài kiểm tra chứng nhận registry', description: 'Các bài kiểm tra OT&E registry yêu cầu.' },
      email: { label: 'E-mail & mẫu thư', description: 'Địa chỉ gửi, thương hiệu và mọi mẫu thư.' },
      sent: { label: 'E-mail đã gửi', description: 'Mọi thư đã gửi hoặc đang chờ, kèm nội dung.' },
      webhooks: { label: 'Gửi sự kiện', description: 'Sự kiện có ký gửi tới hệ thống của bạn (ví dụ CRM).' },
      reportingApi: { label: 'API báo cáo', description: 'Cho Twenty CRM và báo cáo nội bộ đọc dữ liệu kinh doanh, chỉ đọc: nơi nhận, khoá và lịch sử dùng.' },
      staff: { label: 'Nhân viên', description: 'Tài khoản nhân viên, đăng nhập hai bước và vai trò.' },
      roles: { label: 'Vai trò & quyền', description: 'Mỗi vai trò được làm gì.' },
      apiKeys: { label: 'Khoá truy cập', description: 'Khoá cấp cho khách hàng dùng trong hệ thống của họ.' },
      logRetention: { label: 'Lưu trữ log', description: 'Số ngày giữ log trao đổi với registry và mọi log khác trong kho log.' },
      approvals: { label: 'Phê duyệt', description: 'Thay đổi đang chờ người thứ hai duyệt.' },
      audit: { label: 'Nhật ký thao tác', description: 'Ai đã làm gì, khi nào và vì sao.' },
      requirements: { label: 'Bộ giấy tờ', description: 'Giấy tờ cần cho từng trường hợp đổi tên, chuyển nhượng.' },
      declarations: { label: 'Mẫu bản khai', description: 'Mẫu bản khai đăng ký tên miền và cách trình bày.' },
      vnZones: { label: 'Quy định đuôi .vn', description: 'Ai được đăng ký dưới từng đuôi .vn.' },
      trustAnchors: { label: 'Chứng chỉ gốc tin cậy', description: 'Chứng chỉ được chấp nhận cho chữ ký số.' },
      documentTypes: { label: 'Loại hồ sơ', description: 'Các loại hồ sơ của tên miền: ai tải lên, định dạng tệp, đuôi áp dụng.' },
      smartca: { label: 'SmartCA', description: 'Ký số của nhà đăng ký bằng VNPT SmartCA: kết nối, dịch vụ ký và giờ ký hằng ngày.' },
      modulesRegistry: { label: 'Module: tên miền', description: 'Registry và nhà đăng ký đối tác dưới dạng module: kết nối, kiểm tra và nhật ký.' },
      modulesPayment: { label: 'Module: thanh toán', description: 'Nhà cung cấp thanh toán: mã QR chuyển khoản, báo tiền vào, địa chỉ callback, nhật ký.' },
      modulesNotify: { label: 'Module: thông báo', description: 'Kết nối e-mail và Zalo dùng để gửi thông báo.' },
      modulesEinvoice: { label: 'Module: hoá đơn điện tử', description: 'Nhà cung cấp hoá đơn điện tử: tài khoản, mẫu và ký hiệu hoá đơn.' },
      modulesCaptcha: { label: 'Module: chống robot', description: 'Bước xác minh "không phải robot" ở trang đăng nhập của khách.' },
      modulesSigning: { label: 'Module: ký số', description: 'Dịch vụ ký số của nhà đăng ký.' },
      modulesEkyc: { label: 'Module: eKYC', description: 'Xác minh danh tính chủ thể.' },
      modulesOcr: { label: 'Module: đọc giấy tờ (OCR)', description: 'Đọc giấy tờ tuỳ thân trên máy chủ của chúng ta.' },
      modulesRegulatory: { label: 'Module: báo cáo VNNIC', description: 'Báo cáo và hồ sơ gửi VNNIC.' },
      modulesLookup: { label: 'Module: tra cứu tên miền', description: 'Tra cứu WHOIS và tên miền còn trống.' },
      modulesEscrow: { label: 'Module: ký quỹ dữ liệu', description: 'Gửi bản sao dữ liệu đăng ký cho đơn vị ký quỹ.' },
      modulesDns: { label: 'Module: DNS', description: 'Nhà cung cấp máy chủ DNS.' },
      modulesHosting: { label: 'Module: hosting', description: 'Nhà cung cấp hosting.' },
      modulesEmail: { label: 'Module: email doanh nghiệp', description: 'Nhà cung cấp e-mail doanh nghiệp.' },
      modulesCloud: { label: 'Module: cloud, VPS', description: 'Nhà cung cấp máy chủ cloud và VPS.' },
      modulesWebsite: { label: 'Module: website', description: 'Nhà cung cấp công cụ làm website.' },
      modulesAddons: { label: 'Module: tiện ích', description: 'Tính năng bổ sung cài thêm vào hệ thống.' },
      zalo: { label: 'Thông báo qua Zalo', description: 'Thông báo nào gửi thêm qua Zalo bằng mẫu nào, gửi thử, tin đã gửi, số không nhận tin.' },
      captcha: { label: 'Chống robot', description: 'Bước xác minh không phải robot (Cloudflare Turnstile) trên các trang đăng nhập và đăng ký của khách hàng: khoá, khi nào hỏi, bật/tắt.' },
      declarationChecks: { label: 'Kiểm tra bản khai', description: 'Bản khai không đạt kiểm tra: nhân viên xem hay tự động từ chối.' },
      policies: { label: 'Chính sách', description: 'Chính sách tuân thủ theo phiên bản và ngày hiệu lực.' },
      rdap: { label: 'Tra cứu công khai (RDAP)', description: 'Tra cứu công khai hiển thị gì về tên miền.' },
      deadlines: { label: 'Hạn báo cáo', description: 'Báo cáo phải nộp cho VNNIC và ICANN.' },
    },
  },
  command: {
    actions: 'Thao tác',
    settings: 'Cài đặt',
    registerFor: 'Đăng ký tên miền cho {{name}}',
    openCustomer: 'Mở khách hàng {{name}}',
    domainAction: '{{action}} · {{domain}}',
    openTld: 'Mở TLD .{{zone}}',
    sellTld: 'Thiết lập TLD mới',
    transferIn: 'Chuyển tên miền về từ nhà đăng ký khác',
    domainActions: {
      restore: 'Khôi phục',
      transferIn: 'Chuyển tên miền về',
      renew: 'Gia hạn',
      nameservers: 'Đổi máy chủ tên',
      dnssec: 'DNSSEC',
      lock: 'Khoá / mở khoá chuyển',
      transferCode: 'Mã chuyển tên miền',
      registrant: 'Đổi chủ thể',
    },
  },
  actionBar: {
    more: 'Thêm',
    related: 'Đi tới',
    attention: 'Cần xử lý',
    attentionCount_one: '{{count}} việc cần xử lý',
    attentionCount_other: '{{count}} việc cần xử lý',
    allClear: 'Không có gì cần xử lý',
    fix: 'Xử lý',
    showAll: 'Xem thêm {{count}} mục',
    showLess: 'Thu gọn',
  },
  domain: {
    badges: {
      locked: 'Đang khoá chuyển',
      unlocked: 'Không khoá chuyển',
      expires: 'Hết hạn {{date}} · {{when}}',
      updated: 'Cập nhật {{at}}',
    },
    status: {
      title: 'Trạng thái & ràng buộc',
      description: 'Mọi thứ có thể chặn một thao tác trên tên miền này.',
      lock: 'Khoá chuyển',
      autoRenew: 'Tự động gia hạn',
      legalLocks: 'Khoá pháp lý',
      registryStatuses: 'Trạng thái tại registry',
    },
    timeline: {
      title: 'Dòng thời gian',
      all: 'Toàn bộ lịch sử',
      failed: 'Không tải được lịch sử.',
      none: 'Chưa có sự kiện.',
    },
    registration: 'Thông tin đăng ký',
    tabs: {
      overview: 'Tổng quan',
      subject: 'Chủ thể & liên hệ',
      dossier: 'Hồ sơ & tuân thủ',
      dns: 'Máy chủ tên & DNSSEC',
      registry: 'Registry & lịch sử',
      billing: 'Dịch vụ & thanh toán',
    },
    row: { action: 'Thao tác', renew: 'Gia hạn', dossier: 'Hồ sơ', open: 'Mở' },
    views: {
      dossier: 'Hồ sơ',
      compliance: 'Tuân thủ',
      vnnic: 'VNNIC',
      registry: 'Lệnh gửi registry',
      history: 'Lịch sử',
      transfers: 'Chuyển tên miền',
      audit: 'Thao tác của nhân viên',
      observability: 'Vết kỹ thuật',
      orders: 'Đơn hàng',
      billing: 'Thanh toán & ví',
      invoices: 'Hoá đơn',
      services: 'Add-on & dịch vụ',
      privacy: 'Ẩn thông tin & công bố',
    },
    edit: 'Sửa',
    contactDrift_one: '{{count}} liên hệ của tên miền đang khác chủ thể',
    contactDrift_other: '{{count}} liên hệ của tên miền đang khác chủ thể',
    cta: {
      registry: 'Xem lệnh gửi registry',
      compliance: 'Mở tuân thủ',
      locks: 'Xem khoá',
      dossier: 'Mở hồ sơ',
      transfers: 'Mở chuyển tên miền',
      billing: 'Mở thanh toán',
      lifecycle: 'Xem hạn dùng',
      renew: 'Gia hạn ngay',
      history: 'Mở lịch sử',
      contacts: 'Xem liên hệ',
    },
    primary: {
      renew: 'Gia hạn',
      nameservers: 'Máy chủ tên',
      lock: 'Khoá chuyển',
      unlock: 'Mở khoá chuyển',
    },
  },
  bulk: {
    selectAll: 'Chọn mọi tên miền trên trang này',
    select: 'Chọn {{name}}',
    selected_one: 'Đã chọn {{count}} tên miền',
    selected_other: 'Đã chọn {{count}} tên miền',
    clear: 'Bỏ chọn',
    actions: {
      renew: 'Gia hạn',
      nameservers: 'Đổi máy chủ tên',
      lock: 'Khoá chuyển',
      unlock: 'Mở khoá chuyển',
      syncContacts: 'Cập nhật liên hệ theo chủ thể',
    },
    title: '{{action}}: {{count}} tên miền',
    description: 'Một lý do và một lần xác nhận cho tất cả; từng tên miền được gửi riêng và bạn thấy kết quả của từng cái.',
    years: 'Số năm',
    nameservers: 'Máy chủ tên (mỗi dòng một máy, ít nhất hai)',
    nameserversHint: 'Máy chủ tên nằm dưới chính tên miền cần địa chỉ IP; đổi các máy đó ở trang tên miền.',
    reason: 'Lý do (lưu trong nhật ký thao tác)',
    confirm: 'Áp dụng cho {{count}} tên miền',
    close: 'Đóng',
    retry_one: 'Thử lại {{count}} mục lỗi',
    retry_other: 'Thử lại {{count}} mục lỗi',
    running: 'Đang xử lý… {{done}} / {{total}}',
    finished: 'Xong: {{ok}} thành công, {{failed}} thất bại.',
    status: { waiting: 'Chờ', running: 'Đang gửi…', ok: 'Xong', failed: 'Thất bại', skipped: 'Bỏ qua' },
    noSubject: 'Tên miền chưa có chủ thể',
    upToDate: 'Đã khớp, không cần cập nhật',
    renewed: 'Đã bắt đầu gia hạn ({{years}} năm)',
    applied: 'Đã áp dụng',
    pending: 'Đã gửi registry',
    startedSync: 'Đang cập nhật liên hệ',
    registrantChange: 'Cần yêu cầu đổi chủ thể',
  },
  views: {
    saved: 'Bộ lọc đã lưu',
    save: 'Lưu bộ lọc này',
    namePrompt: 'Tên bộ lọc',
    remove: 'Xoá bộ lọc {{name}}',
    none: 'Chưa lưu bộ lọc nào: lọc danh sách rồi bấm lưu.',
    local: 'Chỉ lưu trên trình duyệt này',
  },
  bulkList: {
    selectAll: 'Chọn mọi dòng trên trang này',
    select: 'Chọn {{name}}',
    selected_one: 'Đã chọn {{count}} mục',
    selected_other: 'Đã chọn {{count}} mục',
    export: 'Xuất CSV',
    title_one: '{{action}}: {{count}} mục',
    title_other: '{{action}}: {{count}} mục',
    description: 'Một lần xác nhận cho tất cả; từng mục được gửi riêng và bạn thấy kết quả của từng mục.',
    confirm_one: 'Áp dụng cho {{count}} mục',
    confirm_other: 'Áp dụng cho {{count}} mục',
  },
  lists: {
    all: 'Tất cả',
    changed: 'Đã đổi',
    unchanged: 'Đã đúng, không cần đổi',
    csv: { domains: 'Tên miền', currency: 'Tiền tệ' },
    customers: { status: 'Đổi trạng thái', newStatus: 'Trạng thái mới', tier: 'Đổi hạng', newTier: 'Hạng mới' },
    work: {
      take: 'Nhận việc',
      taken: 'Đã nhận',
      alreadyYours: 'Đã là việc của bạn',
      assign: 'Giao cho…',
      assignee: 'Giao cho',
      assigned: 'Đã giao',
      close: 'Đóng việc (đã xử lý)',
      done: 'Đã đóng',
      closed: 'Việc đã đóng',
      reviewOne: 'Việc cần duyệt được quyết định từng việc',
      onRequest: 'Tự đóng khi yêu cầu được quyết định',
    },
  },
  revenue: {
    title: 'Doanh thu theo tháng',
    description: 'Ghi nhận trong sổ cái, trước thuế, đã trừ hoàn tiền · VND · tháng theo giờ Việt Nam',
    thisMonth: 'Tháng này ({{month}})',
    vsLast: '{{pct}} so với tháng trước',
    noPrevious: 'Tháng trước chưa có doanh thu để so sánh',
    others: 'Doanh thu bằng {{list}} không tính ở đây.',
    showTable: 'Bảng số liệu',
    showChart: 'Biểu đồ',
    openLedger: 'Mở sổ cái',
    loadError: 'Không tải được doanh thu',
    month: 'Tháng',
    amount: 'Doanh thu',
    chartLabel: 'Doanh thu từng tháng trong 12 tháng gần nhất',
  },
  customer: {
    domainsBadge_one: '{{count}} tên miền',
    domainsBadge_other: '{{count}} tên miền',
    dossierCol: 'Hồ sơ',
    dossierOpen_one: '{{count}} chưa đủ',
    dossierOpen_other: '{{count}} chưa đủ',
    dossierDone: 'Đủ',
    tabs: { account: 'Thông tin tài khoản', domains: 'Tên miền ({{count}})', subjects: 'Chủ thể & hồ sơ ({{count}})', users: 'Người dùng ({{count}})', billing: 'Thanh toán & ví' },
    primary: { register: 'Đăng ký tên miền', newService: 'Tạo dịch vụ', newSubject: 'Tạo chủ thể', edit: 'Sửa' },
    more: { addUser: 'Thêm người dùng đăng nhập' },
    attention: {
      suspended: 'Tài khoản không hoạt động: không đăng ký thêm tên miền được.',
      unverified_one: '{{count}} chủ thể chưa được xác minh.',
      unverified_other: '{{count}} chủ thể chưa được xác minh.',
      dossiers_one: '{{count}} tên miền có hồ sơ chưa đủ.',
      dossiers_other: '{{count}} tên miền có hồ sơ chưa đủ.',
      expiring_one: '{{count}} tên miền hết hạn trong 30 ngày.',
      expiring_other: '{{count}} tên miền hết hạn trong 30 ngày.',
      stopped_one: '{{count}} tên miền không hoạt động (dừng, hết hạn hoặc lỗi).',
      stopped_other: '{{count}} tên miền không hoạt động (dừng, hết hạn hoặc lỗi).',
      lowWallet: 'Ví không còn số dư khả dụng.',
      openSubjects: 'Xem chủ thể',
      openDomains: 'Xem tên miền',
      openBilling: 'Mở ví',
      edit: 'Sửa tài khoản',
    },
  },
};
